import React from 'react';
import PropTypes from 'prop-types';

import {Avatar, Box, Card, CardContent, Chip, Stack, Tooltip, Typography} from 'views/components';
import {EmailOutlined as EmailIcon, PhoneOutlined as PhoneIcon} from 'views/icons';
import {HoverToolBox, Translate, translate} from 'views/module';

const ContactLine = (props) => {
  const {icon: Icon, value, emptyExpression} = props;

  return (
    <Stack direction='row' spacing={1} sx={{alignItems: 'center', minWidth: 0}}>
      <Icon sx={{fontSize: '18px', color: 'text.secondary'}} />
      <Typography
        sx={{
          fontSize: '14px',
          color: value ? 'text.primary' : 'text.secondary',
          overflowWrap: 'anywhere',
        }}
      >
        {value || translate(emptyExpression)}
      </Typography>
    </Stack>
  );
};

ContactLine.propTypes = {
  icon: PropTypes.elementType.isRequired,
  value: PropTypes.string,
  emptyExpression: PropTypes.string.isRequired,
};

const FamilyMemberCard = (props) => {
  const {member, onEdit, onRemove} = props;

  const [mouseOver, setMouseOver] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const initials = `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();

  return (
    <Card
      variant='outlined'
      onMouseOver={(e) => {
        setMouseOver(true);
        setAnchorEl(e.currentTarget);
      }}
      onMouseLeave={() => setMouseOver(false)}
      sx={{
        height: '100%',
        borderRadius: '12px',
        borderColor: '#E8E4DE',
        backgroundColor: mouseOver && member.editable ? 'background.highlighted' : 'inherit',
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction='row' spacing={1.5} sx={{alignItems: 'center'}}>
            <Avatar sx={{width: 40, height: 40, fontSize: '14px'}}>
              {initials}
            </Avatar>
            <Box sx={{flex: 1, minWidth: 0}}>
              <Typography noWrap sx={{fontWeight: 600}}>
                {`${member.firstName} ${member.lastName}`}
              </Typography>
              {
                member.relationship &&
                <Typography sx={{fontSize: '14px', color: 'text.secondary'}}>
                  <Translate expression={`family.FamilyMembers.Relationship.${member.relationship}`} />
                </Typography>
              }
            </Box>
            {
              member.priority !== null &&
              <Tooltip title={translate('family.FamilyMembers.PriorityTooltip')}>
                <Chip size='small' label={`#${member.priority}`} />
              </Tooltip>
            }
          </Stack>
          {
            (member.primary || member.roles.length > 0) &&
            <Stack direction='row' sx={{flexWrap: 'wrap', gap: 0.5}}>
              {
                member.primary &&
                <Tooltip title={translate('family.FamilyMembers.PrimaryContactTooltip')}>
                  <Chip size='small' color='primary' label={translate('family.FamilyMembers.PrimaryContact')} />
                </Tooltip>
              }
              {
                member.roles.map((role) => (
                  <Chip
                    key={role}
                    size='small'
                    variant='outlined'
                    label={translate(`family.FamilyMembers.Role.${role}`)}
                  />
                ))
              }
            </Stack>
          }
          <Stack spacing={0.5}>
            <ContactLine icon={EmailIcon} value={member.email} emptyExpression='family.FamilyMembers.NoEmail' />
            <ContactLine icon={PhoneIcon} value={member.phoneNumber} emptyExpression='family.FamilyMembers.NoPhone' />
          </Stack>
          {
            !member.editable &&
            <Typography sx={{fontSize: '12px', color: 'text.secondary'}}>
              <Translate expression='family.FamilyMembers.NotEditable' />
            </Typography>
          }
        </Stack>
      </CardContent>
      {
        member.editable &&
        <HoverToolBox
          toolBar={mouseOver}
          anchorEl={anchorEl}
          onClickEdit={() => onEdit(member)}
          onClickDelete={(setIsDeleting) => onRemove(member, setIsDeleting)}
          hideTool={{
            emoji: true,
            copy: true,
            edit: false,
            delete: member.primary,
          }}
        />
      }
    </Card>
  );
};

FamilyMemberCard.propTypes = {
  member: PropTypes.shape({
    familyMemberId: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    relationship: PropTypes.string,
    priority: PropTypes.number,
    roles: PropTypes.arrayOf(PropTypes.string).isRequired,
    editable: PropTypes.bool.isRequired,
    primary: PropTypes.bool.isRequired,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default React.memo(FamilyMemberCard);
